/* Bundle bar: holds chosen variants in the browser until "Add to cart" pushes them all to the Shopify cart. */
(function () {
  const STORAGE_KEY = 'ss-bundle-bar-v1';

  function loadState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch (e) { return []; }
  }
  function saveState(items) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (e) { /* ignore */ }
  }

  const SOCK_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3h7v9l3.5 3.5a3.5 3.5 0 0 1-5 5L8 15z"/><path d="M8 3v12"/></svg>';
  const TRUCK_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.6"/><circle cx="17.5" cy="18" r="1.6"/></svg>';
  const TRASH_ICON ='<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/></svg>';

  class BundleBar extends HTMLElement {
    constructor() {
      super();
      this.items = loadState();
      try {
        this.config = JSON.parse(this.querySelector('[data-bundle-config]').textContent);
      } catch (e) { this.config = { labels: [], messages: [] }; }
      this.config.labels = (this.config.labels || []).map((l) => l.trim());
      this.config.messages = (this.config.messages || []).map((m) => m.trim());
      this.slotCount = parseInt(this.dataset.slotCount, 10) || 5;
      this.allowOverflow = this.dataset.allowOverflow === 'true';
      this.showWhenEmpty = this.dataset.showWhenEmpty === 'true';

      this.headline = this.querySelector('[data-headline]');
      this.countLabel = this.querySelector('[data-count-label]');
      this.shipping = this.querySelector('[data-shipping]');
      this.freeShipping = parseInt(this.dataset.freeShipping, 10) || 0;
      this.freeShippingItems = parseInt(this.dataset.freeShippingItems, 10) || 0;
      this.slotsEl = this.querySelector('[data-slots]');
      this.progress = this.querySelector('[data-progress]');
      this.cta = this.querySelector('[data-add-all]');
      this.panel = this.querySelector('[data-panel]');
      this.list = this.querySelector('[data-list]');
      this.toggle = this.querySelector('[data-toggle]');

      this.toggle.addEventListener('click', () => this.togglePanel());
      this.cta.addEventListener('click', () => this.addAllToCart());
      // Back from the cart page can restore this page from bfcache with the button still loading
      window.addEventListener('pageshow', (e) => { if (e.persisted) { this.items = loadState(); this.render(); this.resetCta(); } });
      this.slotsEl.addEventListener('click', (e) => {
        const circle = e.target.closest('[data-index]');
        if (circle) this.remove(parseInt(circle.dataset.index, 10));
      });
      this.list.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-remove]');
        if (btn) this.remove(parseInt(btn.dataset.remove, 10));
      });
      window.addEventListener('storage', (e) => { if (e.key === STORAGE_KEY) { this.items = loadState(); this.render(); } });

      this.render();
      this.trackDock();
    }

    /* keep the bar above the theme's mobile dock and the product page's sticky add-to-cart bar */
    trackDock() {
      const dock = document.querySelector('mobile-dock.mobile-dock');
      const sticky = document.querySelector('animate-sticky.sticky-cart-wrapper');
      if (!dock && !sticky) return;
      const update = () => {
        let offset = 0;
        if (dock && dock.classList.contains('is-active') && getComputedStyle(dock).display !== 'none') {
          offset += dock.getBoundingClientRect().height;
        }
        if (sticky && sticky.hasAttribute('animate')) {
          const bar = sticky.querySelector('.product-sticky-cart');
          if (bar && getComputedStyle(bar).display !== 'none') offset += bar.getBoundingClientRect().height;
        }
        document.documentElement.style.setProperty('--bb-dock', Math.round(offset) + 'px');
      };
      update();
      if (dock) new MutationObserver(update).observe(dock, { attributes: true, attributeFilter: ['class'] });
      if (sticky) new MutationObserver(update).observe(sticky, { attributes: true, attributeFilter: ['animate'] });
      window.addEventListener('resize', update);
    }

    get isFull() {
      return !this.allowOverflow && this.items.length >= this.slotCount;
    }

    /* item: { variantId, title, variantTitle, image, priceText, url } */
    add(item) {
      if (!item || !item.variantId) return false;
      if (this.isFull) { this.toast(this.dataset.fullText || 'Bundle full'); return false; }
      this.items.push(item);
      saveState(this.items);
      this.render();
      this.toast('Added to your bundle');
      document.dispatchEvent(new CustomEvent('bundlebar:changed', { detail: { items: this.items } }));
      return true;
    }

    remove(index) {
      if (index < 0 || index >= this.items.length) return;
      this.items.splice(index, 1);
      saveState(this.items);
      this.render();
      document.dispatchEvent(new CustomEvent('bundlebar:changed', { detail: { items: this.items } }));
    }

    clear() {
      this.items = [];
      saveState(this.items);
      this.render();
      document.dispatchEvent(new CustomEvent('bundlebar:changed', { detail: { items: this.items } }));
    }

    togglePanel(force) {
      const open = typeof force === 'boolean' ? force : this.panel.hidden;
      this.panel.hidden = !open;
      this.toggle.setAttribute('aria-expanded', String(open));
    }

    render() {
      // The deal is advertised as "3rd pair free" and "5th pair free", so the FREE markers
      // always sit under slots 3 and 5. The pairs that actually go free are the cheapest
      // ones (that is what checkout discounts), so those are moved into those two slots and
      // the rest fill the remaining slots, dearest first.
      const n = this.items.length;
      const earned = n >= 5 ? 2 : (n >= 3 ? 1 : 0);
      const freeSlots = [2, 4].slice(0, earned).filter((pos) => pos < n);
      if (n) {
        const byPrice = this.items.slice().sort((a, b) => (b.priceCents || 0) - (a.priceCents || 0));
        const cheapest = byPrice.slice(n - freeSlots.length);
        const rest = byPrice.slice(0, n - freeSlots.length);
        const arranged = new Array(n);
        freeSlots.forEach((pos, k) => { arranged[pos] = cheapest[k]; });
        let next = 0;
        for (let i = 0; i < n; i++) if (!arranged[i]) arranged[i] = rest[next++];
        this.items = arranged;
      }
      const freeSet = new Set(freeSlots);
      // the FREE markers under slots 3 and 5 always show, even on an empty bar
      const labelSlots = new Set([2, 4].filter((pos) => pos < Math.max(this.slotCount, n)));
      const visible = n > 0 || this.showWhenEmpty;
      this.classList.toggle('bundle-bar--hidden', !visible);
      document.body.classList.toggle('bundle-bar-visible', visible);

      // headline
      const msgs = this.config.messages.filter((m) => m.trim() !== '');
      let msg = msgs.length ? msgs[Math.min(n, msgs.length - 1)] : '';
      // {free} = free pairs earned (1 per 3 items), {saved} = value of the cheapest free pairs
      const freeCount = freeSlots.length;
      const prices = this.items.map((it) => it.priceCents || 0).sort((a, b) => a - b);
      const saved = prices.slice(0, Math.min(freeCount, prices.length)).reduce((a, b) => a + b, 0);
      msg = msg.replace('{free}', String(freeCount)).replace('{saved}', this.money(saved));
      this.headline.textContent = msg;

      // free shipping: once the payable total (after free pairs) reaches the threshold, or from N pairs
      if (this.shipping) {
        const pay = prices.reduce((a, b) => a + b, 0) - saved;
        const progressText = (this.dataset.freeShippingProgressText || '').trim();
        const enabled = this.freeShipping > 0 || this.freeShippingItems > 0;
        const unlocked = (this.freeShipping > 0 && pay >= this.freeShipping) || (this.freeShippingItems > 0 && n >= this.freeShippingItems);
        if (enabled && n > 0 && unlocked) {
          this.shipping.innerHTML = TRUCK_ICON + '<span>' + escapeHtml(this.dataset.freeShippingText || 'Free shipping unlocked') + '</span>';
          this.shipping.classList.add('bundle-bar__shipping--unlocked');
          this.shipping.hidden = false;
        } else if (this.freeShipping > 0 && n > 0 && progressText) {
          this.shipping.textContent = progressText.replace('{amount}', this.money(this.freeShipping - pay));
          this.shipping.classList.remove('bundle-bar__shipping--unlocked');
          this.shipping.hidden = false;
        } else {
          this.shipping.hidden = true;
        }
      }

      // count
      this.countLabel.textContent = n + ' ' + (n === 1 ? this.dataset.itemText : this.dataset.itemsText);

      // slots
      const total = Math.max(this.slotCount, n);
      let html = '';
      for (let i = 0; i < total; i++) {
        const item = this.items[i];
        // items are sorted dearest first, so the free pairs are the last filled slots
        const isFreeSlot = labelSlots.has(i);
        const label = isFreeSlot ? (this.dataset.freeText || 'FREE') : '';
        // numbered sock outline in every circle (filled ones just look solid)
        // empty slot: number + the Sole Sox artwork; filled slot: the chosen shoe
        const artwork = this.dataset.slotImage
          ? '<img class="bundle-bar__art" src="' + escapeHtml(this.dataset.slotImage) + '" alt="" loading="lazy">'
          : SOCK_ICON;
        const face = '<span class="bundle-bar__num">' + (i + 1) + '</span>' + artwork;
        if (item) {
          html += '<div class="bundle-bar__slot bundle-bar__slot--filled">' +
            '<button type="button" class="bundle-bar__circle" data-index="' + i + '" title="Remove ' + escapeHtml(item.title) + '" aria-label="Remove ' + escapeHtml(item.title) + '">' +
            // show the chosen shoe once a pair is picked; the numbered outline is for empty slots
            (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="">' : face) +
            '</button>' +
            '<span class="bundle-bar__slot-label bundle-bar__slot-label--free">' + escapeHtml(label) + '</span></div>';
        } else {
          html += '<div class="bundle-bar__slot"><span class="bundle-bar__circle">' + face + '</span>' +
            '<span class="bundle-bar__slot-label">' + escapeHtml(label) + '</span></div>';
        }
      }
      this.slotsEl.innerHTML = html;

      // progress line
      const pct = total > 1 ? Math.min(n, total) / total * 100 : (n ? 100 : 0);
      this.progress.style.width = pct + '%';

      // cta
      this.cta.disabled = n === 0;
      // show the payable total on the button (after free pairs)
      const ctaText = this.querySelector('[data-cta-text]');
      const ctaTotal = this.querySelector('[data-cta-total]');
      if (ctaTotal) {
        const payable = prices.reduce((a, b) => a + b, 0) - saved;
        ctaTotal.textContent = n > 0 ? this.money(payable) : '';
        ctaTotal.hidden = n === 0;
        if (ctaText) ctaText.dataset.hasTotal = n > 0 ? 'true' : 'false';
      }

      // panel list
      if (n === 0) {
        this.list.innerHTML = '<li class="bundle-bar__empty">' + escapeHtml(this.dataset.emptyText || '') + '</li>';
      } else {
        // the free pairs sit in the advertised 3rd / 5th slots
        const freeIdx = freeSet;
        this.list.innerHTML = this.items.map((item, i) => {
          const priceText = item.priceText || (item.priceCents ? this.money(item.priceCents) : '');
          const isFree = freeIdx.has(i);
          return '<li class="bundle-bar__row' + (isFree ? ' bundle-bar__row--free' : '') + '">' +
          (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="">' : '') +
          '<span class="bundle-bar__row-title">' + escapeHtml(item.title) +
          (item.variantTitle ? ' <small>· ' + escapeHtml(item.variantTitle) + '</small>' : '') + '</span>' +
          (priceText ? '<span class="bundle-bar__row-price">' + (isFree ? '<s>' + escapeHtml(priceText) + '</s> <b>' + escapeHtml(this.dataset.freeText || 'FREE') + '</b>' : escapeHtml(priceText)) + '</span>' : '') +
          '<button type="button" class="bundle-bar__remove" data-remove="' + i + '" aria-label="Remove">' + TRASH_ICON + '</button>' +
          '</li>';
        }).join('');
      }
    }

    async addAllToCart() {
      if (!this.items.length) return;
      this.cta.classList.add('loading');
      this.cta.disabled = true;

      // merge duplicate variants into quantities
      const qty = {};
      this.items.forEach((it) => { qty[it.variantId] = (qty[it.variantId] || 0) + 1; });
      const lines = Object.keys(qty).map((id) => ({ id: parseInt(id, 10), quantity: qty[id] }));

      try {
        const res = await fetch(this.dataset.cartUrl, {
          ...fetchConfig('javascript'),
          body: JSON.stringify({ items: lines })
        });
        const state = await res.json();
        if (state.status && state.status !== 200) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: state.description || state.message } }));
          this.toast(state.description || state.message || 'Could not add to cart');
          this.resetCta();
          return;
        }
        // Checkout empties the bar and opens the cart drawer straight away. Unicorn redirects /cart to
        // its drawer anyway, so opening it in place avoids a page reload.
        this.clear();
        this.togglePanel(false);
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: state } }));
        const opened = window.ssOpenCart ? await window.ssOpenCart() : false;
        if (!opened) window.location.href = this.dataset.cartPageUrl || '/cart';
        this.resetCta();
      } catch (e) {
        console.error('bundle bar: add all failed', e);
        this.toast('Could not add to cart');
        this.resetCta();
      }
    }

    resetCta() {
      this.cta.classList.remove('loading');
      this.cta.disabled = this.items.length === 0;
    }

    money(cents) {
      const fmt = (window.theme && theme.shopSettings && theme.shopSettings.moneyFormat) || this.dataset.moneyFormat || '${{amount}}';
      try { return theme.Currency.formatMoney(cents, fmt); } catch (e) { return fmt.replace(/\{\{\s*amount\w*\s*\}\}/, (cents / 100).toFixed(2)); }
    }

    toast(text) {
      let el = document.querySelector('.bundle-bar__toast');
      if (!el) {
        el = document.createElement('div');
        el.className = 'bundle-bar__toast';
        document.body.appendChild(el);
      }
      el.textContent = text;
      el.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => el.classList.remove('is-visible'), 1800);
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  customElements.define('bundle-bar', BundleBar);

  window.BundleBar = {
    get el() { return document.querySelector('bundle-bar'); },
    add(item) { const el = this.el; return el ? el.add(item) : false; },
    get items() { const el = this.el; return el ? el.items : []; },
    get isFull() { const el = this.el; return el ? el.isFull : false; }
  };

  /* ---------- Product page: route the main "Add to cart" form into the bar ---------- */
  function moneyFormat(cents) {
    try { return theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat); } catch (e) { return ''; }
  }

  document.addEventListener('submit', async (evt) => {
    const form = evt.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('action') !== theme.routes.cart_add_url && !/\/cart\/add/.test(form.action)) return;
    if (!form.closest('product-form')) return;
    // Bundles / gift cards are not part of the promotion: let them add straight to the cart
    if (form.closest('[data-bundle-exclude]')) return;
    const bar = document.querySelector('bundle-bar');
    if (!bar) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    const variantId = form.querySelector('[name="id"]') && form.querySelector('[name="id"]').value;
    if (!variantId) return;
    const quantity = parseInt((form.querySelector('[name="quantity"]') || {}).value, 10) || 1;
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    const handleEl = form.closest('[data-product-handle]');
    const handle = (handleEl && handleEl.dataset.productHandle) || (match && match[1]);
    if (!handle) return;

    const button = form.querySelector('[type="submit"]');
    if (button) button.classList.add('loading');
    try {
      const product = await fetch('/products/' + handle + '.js').then((r) => r.json());
      const variant = product.variants.find((v) => String(v.id) === String(variantId));
      if (!variant) return;
      const image = (variant.featured_image && variant.featured_image.src) || (product.images && product.images[0]) || '';
      for (let i = 0; i < quantity; i++) {
        bar.add({
          variantId: variant.id,
          title: product.title,
          variantTitle: variant.title === 'Default Title' ? '' : variant.title,
          image: image ? image.replace(/(\.[a-z]+)(\?|$)/i, '_200x200$1$2') : '',
          priceText: moneyFormat(variant.price),
          priceCents: variant.price,
          url: '/products/' + handle
        });
      }
    } catch (e) {
      console.error('bundle bar: product form', e);
    } finally {
      if (button) button.classList.remove('loading');
    }
  }, true);
})();
